import { useState, useEffect } from "react";
import { AlertTriangle, ArrowUpRight, ArrowRight } from "lucide-react";
import { useOrgId } from "../hooks/useOrgId";
import * as api from "../lib/api";

const PRIMARY = "#018D4C";

function SimilarityBadge({ score }) {
  const pct = Math.round(score * 100);
  const getStyle = () => {
    if (score >= 0.95) return { bg: "#FF2A00", label: "EXTREME" };
    if (score >= 0.9) return { bg: "#FF2A00", label: "HIGH" };
    if (score >= 0.85) return { bg: "#FF8C00", label: "MODERATE" };
    return { bg: "#71717A", label: "LOW" };
  };
  const { bg, label } = getStyle();
  return (
    <div className="flex flex-col items-center gap-1 px-4">
      <span className="text-2xl font-bold text-zinc-900">
        {pct}%
      </span>
      <span className="text-[9px] tracking-[0.2em] font-bold" style={{ color: bg }}>{label}</span>
    </div>
  );
}

function PairCard({ pair }) {
  const { article1: a1, article2: a2, similarity_score: score } = pair;
  return (
    <div
      data-testid="cannibalization-pair"
      className="bg-white rounded-xl border border-zinc-100 hover:border-red-200 transition-colors shadow-sm"
    >
      <div className="flex items-stretch">
        {/* Article 1 */}
        <div className="flex-1 p-4">
          <p className="text-[10px] tracking-widest uppercase text-zinc-400 font-bold mb-1">Article A</p>
          <p className="text-sm font-bold text-zinc-900 line-clamp-2 mb-2">{a1.title}</p>
          {a1.categories?.length > 0 && (
            <p className="text-xs text-zinc-400">{a1.categories.slice(0, 2).join(", ")}</p>
          )}
          <div className="flex items-center gap-2 mt-3">
            {a1.cluster_id && (
              <span className="px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase bg-zinc-100 text-zinc-500 rounded-full">
                {a1.cluster_id}
              </span>
            )}
            {a1.wp_url && (
              <a
                href={a1.wp_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[10px] font-bold hover:underline"
                data-testid="cannibal-article1-link"
                style={{ color: PRIMARY }}
              >
                View <ArrowUpRight size={10} />
              </a>
            )}
          </div>
        </div>

        {/* Similarity score */}
        <div className="flex flex-col items-center justify-center border-x border-zinc-100 py-4">
          <SimilarityBadge score={score} />
          <ArrowRight size={14} color="#D4D4D4" className="mt-2" />
        </div>

        {/* Article 2 */}
        <div className="flex-1 p-4">
          <p className="text-[10px] tracking-widest uppercase text-zinc-400 font-bold mb-1">Article B</p>
          <p className="text-sm font-bold text-zinc-900 line-clamp-2 mb-2">{a2.title}</p>
          {a2.categories?.length > 0 && (
            <p className="text-xs text-zinc-400">{a2.categories.slice(0, 2).join(", ")}</p>
          )}
          <div className="flex items-center gap-2 mt-3">
            {a2.cluster_id && (
              <span className="px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase bg-zinc-100 text-zinc-500 rounded-full">
                {a2.cluster_id}
              </span>
            )}
            {a2.wp_url && (
              <a
                href={a2.wp_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[10px] font-bold hover:underline"
                data-testid="cannibal-article2-link"
                style={{ color: PRIMARY }}
              >
                View <ArrowUpRight size={10} />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Cannibalization() {
  const orgId = useOrgId();
  const [data, setData] = useState({ pairs: [], total: 0 });
  const [threshold, setThreshold] = useState(0.85);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.getCannibalization(threshold)
      .then(d => setData(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [threshold, orgId]);

  return (
    <div data-testid="cannibalization-page" className="p-6 sm:p-8 max-w-5xl mx-auto">
      <div className="mb-8 pb-6 border-b border-zinc-100 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-1">Content Quality</p>
          <h1 className="text-4xl font-bold text-zinc-900 tracking-tight">
            Cannibalization
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Articles competing for the same topic (similarity ≥ threshold)</p>
        </div>

        {/* Threshold control */}
        <div className="flex items-center gap-3">
          <label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Threshold</label>
          <div className="flex gap-1">
            {[0.75, 0.8, 0.85, 0.9, 0.95].map(v => (
              <button
                key={v}
                data-testid={`threshold-${v}`}
                onClick={() => setThreshold(v)}
                className={`px-3 py-1.5 text-xs font-bold transition-colors rounded-lg ${
                  threshold === v ? "bg-red-500 text-white" : "bg-white border border-zinc-100 text-zinc-500 hover:bg-zinc-50"
                }`}
              >
                {Math.round(v * 100)}%
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Stats banner */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: "Pairs Found", val: data.total, color: "#FF2A00" },
          { label: "Threshold", val: `${Math.round(threshold * 100)}%`, color: "#FF8C00" },
          { label: "Action Needed", val: data.total > 0 ? "Yes" : "No", color: data.total > 0 ? "#FF2A00" : "#008A33" },
        ].map(({ label, val, color }) => (
          <div key={label} className="bg-white rounded-xl border border-zinc-100 px-4 py-3 shadow-sm">
            <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">{label}</p>
            <p className="text-2xl font-bold mt-1" style={{ color }}>{val}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-zinc-400 py-8">Analyzing content similarity...</div>
      ) : data.pairs.length === 0 ? (
        <div className="bg-white rounded-xl border border-zinc-100 p-12 text-center shadow-sm">
          <AlertTriangle size={40} color="#CCC" className="mx-auto" />
          <p className="text-sm font-bold text-green-600 mt-4">No cannibalization detected!</p>
          <p className="text-xs text-zinc-500 mt-1">
            {data.total === 0
              ? "Sync your articles first, or lower the threshold to find more matches."
              : `No articles with similarity ≥ ${Math.round(threshold * 100)}%.`}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.pairs.map((pair, i) => (
            <PairCard key={i} pair={pair} />
          ))}
        </div>
      )}
    </div>
  );
}
